/**
 * Evidence pack preview MVP (plan §24.4.4 three-column layout).
 *
 * Read-only — no export wiring. The shipping `EvidencePackBuilder`
 * lands at W10 once Review DO + R2 hash + audience-slice manifest are
 * online. Until then this gives the OEM director "what would I take
 * away?" eye candy WITHOUT pretending to ship a verdict.
 *
 * Layout:
 *   ┌─────────┬─────────────┬──────────────┐
 *   │ Scope   │ Preview     │ Citation     │
 *   │ 25 %    │ 45 %        │ ledger 30 %  │
 *   └─────────┴─────────────┴──────────────┘
 *   ── 3 disabled Export buttons (PDF / ZIP / JSON) ──
 *
 * Audience profile selector is a chip-bar stub: default "Internal
 * Compliance Lead". The 3 audience values are pinned in
 * contracts/AudienceProfile, but slicing logic lives in W10 — here we
 * only read the current selection to annotate the preview body.
 */

import { useMemo, useState } from "react";
import type { ComplianceRequirementCard, LegalReference, LifecycleLayer } from "@regpulse/contracts";
import {
  buildEvidencePack,
  type CoverageClaimResponse,
  type SignalBundleMeta,
} from "../services/api";

type Audience = "InternalComplianceLead" | "ExternalReviewerAuditor" | "ExternalAuthority";

const AUDIENCE_LABEL: Record<Audience, string> = {
  InternalComplianceLead: "Internal · Compliance Lead",
  ExternalReviewerAuditor: "External · Reviewer / Auditor",
  ExternalAuthority: "External · Authority",
};

const AUDIENCE_HINT: Record<Audience, string> = {
  InternalComplianceLead:
    "Full board + raw outcomes + provenance triplet. Default audience.",
  ExternalReviewerAuditor:
    "Citations + evidence guidance, market-force tone softened. (W10)",
  ExternalAuthority:
    "Hard-law and evidence-standard tracks only. No market_force surface. (W10)",
};

const LAYER_LABEL: Record<LifecycleLayer, string> = {
  A: "A · Design",
  B: "B · Entry",
  C: "C · Sales",
  D: "D · In-use",
  P: "P · Exposure",
  U: "U · Use case",
};

interface Props {
  cards: ComplianceRequirementCard[];
  appliedHash: string | null;
  signalBundle: SignalBundleMeta | null;
  projectName: string;
  /** Whatever the API returned the last time evaluate ran. We embed this
   * verbatim into the JSON pack as `readinessSnapshot`. */
  readinessSnapshot?: unknown;
  /** Linked signals — embedded verbatim into the JSON pack. */
  linkedSignals?: ReadonlyArray<{ ruleId: string; signalIds: string[]; cardTitle: string }>;
  /** Drift-gated declared-scope claim (GET /v1/coverage-claim). Used only to
   * render an advisory source/proof chain for cited core rules. NEVER fed
   * into the pack body or legalReferences. */
  coverageClaim?: CoverageClaimResponse["claim"] | null;
}

export function EvidencePackPreview({
  cards,
  appliedHash,
  signalBundle,
  projectName,
  readinessSnapshot,
  linkedSignals,
  coverageClaim,
}: Props) {
  const [audience, setAudience] = useState<Audience>("InternalComplianceLead");
  // W7-slice-b2: which path produced the LAST exported pack?
  //   - "server-verified" → /v1/evidence-pack/build returned a sha256
  //   - "client-fallback" → server unavailable, client-side JSON only
  //   - "idle" → no export attempted yet (no chip rendered)
  const [hashStatus, setHashStatus] = useState<
    "idle" | "server-verified" | "client-fallback"
  >("idle");

  // Join applicable cards to the drift-gated coverage claim by ruleId. This is
  // an advisory display only — it is NOT written into the evidence pack body
  // or legalReferences (those stay sourced from rule.sources upstream).
  // Invariant: `card.id === rule.stable_id === coverage-claim ruleId`
  // (set in packages/applicability/src/rule-to-card.ts). If a card ever uses a
  // surrogate id, this join yields an empty proof chain rather than wrong data.
  const proofChain = useMemo(() => {
    const core = coverageClaim?.declaredScope.coreRules ?? [];
    if (core.length === 0) return [];
    const byRuleId = new Map(core.map((r) => [r.ruleId, r]));
    return cards.flatMap((c) => {
      if (c.applicabilityCategory !== "applicable") return [];
      const proof = byRuleId.get(c.id);
      return proof ? [{ card: c, proof }] : [];
    });
  }, [cards, coverageClaim]);

  const stats = useMemo(() => {
    const applicable = cards.filter((c) => c.applicabilityCategory === "applicable");
    const hardLaw = applicable.filter((c) => c.obligationKind === "hard_law");
    const evidence = applicable.filter((c) => c.obligationKind === "evidence_standard");
    const market = applicable.filter((c) => c.obligationKind === "market_force");
    const ecosystem = applicable.filter((c) => c.obligationKind === "ecosystem_signal");
    const continuing = applicable.filter((c) => c.continuingObligation !== undefined);
    const placeholder = cards.filter((c) => c.lifecycleState === "PLACEHOLDER");
    const layerCount: Record<LifecycleLayer, number> = { A: 0, B: 0, C: 0, D: 0, P: 0, U: 0 };
    for (const c of applicable) layerCount[c.layer]++;
    return {
      total: cards.length,
      applicable: applicable.length,
      hardLaw: hardLaw.length,
      evidence: evidence.length,
      market: market.length,
      ecosystem: ecosystem.length,
      continuing: continuing.length,
      placeholder: placeholder.length,
      layerCount,
    };
  }, [cards]);

  // Citation ledger — flatten all legal refs across applicable cards,
  // sorted by granularity specificity so the highest-specificity citations
  // float to the top. Each entry also carries the card-level legalEffect
  // so the reader can see "hard force / soft force" at a glance.
  const ledger = useMemo(() => {
    const refs: Array<{
      ref: LegalReference;
      cardId: string;
      legalEffect: ComplianceRequirementCard["legalEffect"];
    }> = [];
    for (const c of cards) {
      if (c.applicabilityCategory !== "applicable") continue;
      for (const r of c.legalReferences) refs.push({ ref: r, cardId: c.id, legalEffect: c.legalEffect });
    }
    const grain = (g: LegalReference["granularity"]) =>
      g === "subparagraph"
        ? 5
        : g === "paragraph"
          ? 4
          : g === "article" || g === "annex"
            ? 3
            : g === "section" || g === "chapter"
              ? 2
              : g === "regulation"
                ? 1
                : 0;
    refs.sort((a, b) => grain(b.ref.granularity) - grain(a.ref.granularity));
    return refs.slice(0, 10);
  }, [cards]);

  return (
    <section
      aria-labelledby="evidence-pack-preview-title"
      data-testid="evidence-pack-preview"
      style={{
        background: "var(--color-surface-elevated)",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-8)",
        padding: "var(--space-12)",
      }}
    >
      <header
        style={{
          marginBottom: "var(--space-12)",
          display: "flex",
          justifyContent: "space-between",
          gap: "var(--space-12)",
          flexWrap: "wrap",
        }}
      >
        <div>
          <h3 id="evidence-pack-preview-title" className="type-h3" style={{ margin: 0, fontSize: 13 }}>
            Evidence pack preview ·{" "}
            <span
              style={{
                fontWeight: 400,
                color: "var(--status-warning, var(--color-text-secondary))",
              }}
            >
              draft, not exportable yet
            </span>
          </h3>
          <p
            className="type-caption"
            style={{ margin: "var(--space-4) 0 0 0", color: "var(--color-text-secondary)" }}
          >
            What the OEM compliance lead would take away as a snapshot. Export
            actions land with the Review DO (W7→W10). Audience slicing is
            advisory until that point.
          </p>
        </div>
      </header>

      <div
        data-testid="evidence-pack-three-col"
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(180px, 25%) minmax(260px, 45%) minmax(220px, 30%)",
          gap: "var(--space-12)",
        }}
      >
        {/* ─── Scope (25%) ──────────────────────────────────────── */}
        <section
          aria-label="Scope"
          data-testid="ep-col-scope"
          style={colStyle}
        >
          <h4 style={colTitle}>Scope</h4>
          <dl style={{ margin: 0, fontSize: 11 }}>
            <div style={dlRow}>
              <dt style={dt}>Audience profile</dt>
              <dd style={dd}>
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {(Object.keys(AUDIENCE_LABEL) as Audience[]).map((a) => (
                    <button
                      key={a}
                      data-testid={`audience-${a}`}
                      data-active={audience === a}
                      onClick={() => setAudience(a)}
                      style={{
                        fontSize: 11,
                        padding: "2px 6px",
                        border:
                          audience === a
                            ? "1px solid var(--color-accent)"
                            : "1px solid var(--color-border)",
                        background:
                          audience === a ? "var(--color-accent-soft)" : "var(--color-surface)",
                        color:
                          audience === a ? "var(--color-accent)" : "var(--color-text-primary)",
                        borderRadius: "var(--radius-4)",
                        textAlign: "left",
                        cursor: "pointer",
                      }}
                    >
                      {AUDIENCE_LABEL[a]}
                    </button>
                  ))}
                </div>
                <p
                  style={{
                    marginTop: 4,
                    fontSize: 10,
                    color: "var(--color-text-secondary)",
                  }}
                >
                  {AUDIENCE_HINT[audience]}
                </p>
              </dd>
            </div>
            <div style={dlRow}>
              <dt style={dt}>Project</dt>
              <dd style={dd}>{projectName || "(unnamed)"}</dd>
            </div>
            <div style={dlRow}>
              <dt style={dt}>Snapshot date</dt>
              <dd style={dd}>{new Date().toISOString().slice(0, 10)} (UTC)</dd>
            </div>
            <div style={dlRow}>
              <dt style={dt}>Config hash</dt>
              <dd style={{ ...dd, fontFamily: "var(--font-mono)" }}>
                {appliedHash ? appliedHash.slice(0, 12) + "…" : "—"}
              </dd>
            </div>
            <div style={dlRow}>
              <dt style={dt}>Signal bundle</dt>
              <dd style={dd}>
                {signalBundle
                  ? `${signalBundle.bundleId} (${signalBundle.count})`
                  : "(none ingested)"}
              </dd>
            </div>
          </dl>
        </section>

        {/* ─── Preview (45%) ────────────────────────────────────── */}
        <section
          aria-label="Preview"
          data-testid="ep-col-preview"
          style={colStyle}
        >
          <h4 style={colTitle}>Pack preview</h4>
          <div
            style={{
              background: "var(--color-surface)",
              border: "1px dashed var(--color-border-strong)",
              borderRadius: "var(--radius-4)",
              padding: "var(--space-8)",
              fontSize: 12,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              EU compliance evidence pack — {projectName || "(unnamed)"}
            </div>
            <p style={{ margin: "0 0 6px 0", lineHeight: 1.4 }}>
              <strong>Executive summary.</strong> {stats.applicable} of{" "}
              {stats.total} canonical rules are applicable to the supplied
              configuration. Of those, {stats.hardLaw} carry hard-law force,{" "}
              {stats.evidence} are evidence standards, and {stats.continuing}{" "}
              carry continuing post-market obligations.{" "}
              {stats.market + stats.ecosystem > 0
                ? `${stats.market + stats.ecosystem} non-legal items (market force / ecosystem) are reported separately and never summed with hard law.`
                : ""}{" "}
              {stats.placeholder > 0
                ? `${stats.placeholder} rule(s) remain PLACEHOLDER and are listed as curation gaps.`
                : ""}
            </p>
            <div
              data-testid="ep-layer-thumbnail"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(6, 1fr)",
                gap: 2,
                marginTop: 8,
              }}
            >
              {(Object.keys(stats.layerCount) as LifecycleLayer[]).map((l) => (
                <div
                  key={l}
                  data-layer-thumb={l}
                  style={{
                    background: "var(--color-surface-sunken)",
                    border: "1px solid var(--color-border)",
                    padding: 4,
                    textAlign: "center",
                    fontSize: 11,
                    borderRadius: 2,
                  }}
                >
                  <div style={{ color: "var(--color-text-secondary)", fontSize: 9 }}>
                    {LAYER_LABEL[l]}
                  </div>
                  <div style={{ fontFamily: "var(--font-mono)", fontWeight: 700 }}>
                    {stats.layerCount[l]}
                  </div>
                </div>
              ))}
            </div>
            <p
              style={{
                fontSize: 10,
                color: "var(--color-text-secondary)",
                marginTop: 8,
                marginBottom: 0,
              }}
            >
              Page 2 …N would carry one obligation per page with full citation
              footnote. See <code>RequirementInspector</code> on the board for
              the OEM-grade per-obligation surface.
            </p>
          </div>
        </section>

        {/* ─── Citation ledger (30%) ────────────────────────────── */}
        <section
          aria-label="Citation ledger"
          data-testid="ep-col-ledger"
          style={colStyle}
        >
          <h4 style={colTitle}>Citation ledger · top {ledger.length}</h4>
          {ledger.length === 0 ? (
            <p style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
              No applicable references in scope.
            </p>
          ) : (
            <ol
              style={{
                listStyle: "decimal",
                paddingLeft: "var(--space-16)",
                margin: 0,
                fontSize: 11,
                display: "flex",
                flexDirection: "column",
                gap: 4,
              }}
            >
              {ledger.map(({ ref, cardId, legalEffect }, i) => (
                <li key={`${cardId}-${i}`} data-testid={`ledger-entry-${i}`}>
                  <div style={{ fontWeight: 600, lineHeight: 1.3 }}>
                    {ref.instrument}
                    {(ref.article || ref.annex || ref.section || ref.paragraph) && (
                      <span style={{ fontWeight: 400 }}>
                        {" "}
                        · {ref.granularity}{" "}
                        {ref.article ?? ref.annex ?? ref.section ?? ref.paragraph}
                      </span>
                    )}
                  </div>
                  <div style={{ color: "var(--color-text-secondary)" }}>
                    {legalEffect} · conf {ref.confidence} · {cardId}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>

      {proofChain.length > 0 && (
        <section
          aria-label="Source and proof chain"
          data-testid="ep-col-proof-chain"
          style={{
            marginTop: "var(--space-12)",
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius-4)",
            padding: "var(--space-12)",
            background: "var(--color-surface)",
          }}
        >
          <h4 style={colTitle}>
            Source &amp; proof chain · advisory, declared-scope evidence
            readiness — not a legal determination
          </h4>
          <p
            style={{
              margin: "0 0 var(--space-8) 0",
              fontSize: 11,
              color: "var(--color-text-secondary)",
            }}
          >
            Retrieved context and registry proof for declared-scope core rules.
            Separate from the citation ledger; never written into the evidence
            pack body or legalReferences. Human review required (ADR-0002).
          </p>
          <ul
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-8)",
              fontSize: 11,
            }}
          >
            {proofChain.map(({ card, proof }) => (
              <li
                key={proof.ruleId}
                data-testid={`proof-chain-${proof.ruleId}`}
                style={{
                  borderLeft: "3px solid var(--ft-ecosystem, #888)",
                  paddingLeft: "var(--space-8)",
                }}
              >
                <div style={{ fontWeight: 600 }}>
                  {proof.ruleId} · {card.title}
                </div>
                <div style={{ color: "var(--color-text-secondary)" }}>
                  {proof.instrument} ({proof.jurisdictionCode})
                </div>
                <div>
                  <a href={proof.officialUrl} target="_blank" rel="noreferrer">
                    Official source
                  </a>{" "}
                  · chunk <code>{proof.chunkId}</code>
                </div>
                <div
                  style={{
                    fontFamily: "var(--font-mono, monospace)",
                    color: "var(--color-text-secondary)",
                    wordBreak: "break-all",
                  }}
                >
                  raw sha256 {proof.rawSha256.slice(0, 12)}… · evidence pack{" "}
                  {proof.evidencePackSha256.slice(0, 12)}…
                </div>
                <div style={{ color: "var(--color-text-secondary)" }}>
                  reviewer {proof.reviewer} · {proof.decision}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <footer
        style={{
          marginTop: "var(--space-12)",
          display: "flex",
          gap: "var(--space-8)",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>Export:</div>
        {hashStatus !== "idle" && (
          <span
            data-testid="ep-hash-chip"
            data-hash-status={hashStatus}
            title={
              hashStatus === "server-verified"
                ? "Server-side SHA-256 over the canonical pack body."
                : "Server unavailable — client-side JSON only; sha256 is null."
            }
            style={{
              fontSize: 10,
              padding: "2px 8px",
              borderRadius: "var(--radius-full)",
              border:
                hashStatus === "server-verified"
                  ? "1px solid var(--ft-evidence)"
                  : "1px dashed var(--color-border-strong)",
              background:
                hashStatus === "server-verified"
                  ? "var(--ft-evidence-soft, var(--color-surface))"
                  : "var(--color-surface-sunken)",
              color:
                hashStatus === "server-verified"
                  ? "var(--ft-evidence)"
                  : "var(--color-text-secondary)",
              fontWeight: 600,
            }}
          >
            {hashStatus === "server-verified" ? "sha256 ✓ verified" : "advisory hash"}
          </span>
        )}
        {(["PDF", "ZIP", "JSON"] as const).map((kind) => {
          const enabled = kind === "JSON";
          return (
            <button
              key={kind}
              data-testid={`ep-export-${kind.toLowerCase()}`}
              disabled={!enabled}
              title={
                enabled
                  ? "Download the evidence pack as a single .json file (no R2 hashing yet — see ADR-0002)"
                  : "Disabled — PDF rendering + ZIP bundling land with R2 hash (W10)"
              }
              onClick={
                enabled
                  ? () =>
                      handleExportJson({
                        projectName,
                        appliedHash,
                        audience,
                        cards,
                        signalBundle,
                        readinessSnapshot,
                        linkedSignals,
                        setHashStatus,
                      })
                  : undefined
              }
              style={
                enabled
                  ? {
                      fontSize: 11,
                      padding: "var(--space-4) var(--space-8)",
                      border: "1px solid var(--ft-hard-law)",
                      background: "var(--ft-hard-law)",
                      color: "var(--color-text-on-accent, white)",
                      borderRadius: "var(--radius-4)",
                      cursor: "pointer",
                      fontWeight: 600,
                    }
                  : {
                      fontSize: 11,
                      padding: "var(--space-4) var(--space-8)",
                      border: "1px dashed var(--color-border-strong)",
                      background: "var(--color-surface-sunken)",
                      color: "var(--color-text-disabled)",
                      borderRadius: "var(--radius-4)",
                      cursor: "not-allowed",
                    }
              }
            >
              Export {kind}
            </button>
          );
        })}
        <div
          style={{
            marginLeft: "auto",
            fontSize: 10,
            color: "var(--color-text-secondary)",
            maxWidth: 360,
            textAlign: "right",
          }}
        >
          Reminder: even when exports go live, the pack is advisory. No row in
          this preview is a regulatory verdict (ADR-0002 / Lesson §25.10).
        </div>
      </footer>
    </section>
  );
}

/**
 * Try server-side build FIRST; fall back to client-side JSON if the
 * worker is unreachable. W7-slice-b2 / ADR-0005. The chip in the UI
 * reflects which path actually produced the file:
 *   - "server-verified" — server returned a real sha256
 *   - "client-fallback" — client built the pack with sha256: null
 *
 * Lesson §16 / ADR-0004 still holds: this function NEVER mutates any
 * canonical state. Both paths are read-only with respect to the
 * registry.
 */
async function handleExportJson(input: {
  projectName: string;
  appliedHash: string | null;
  audience: Audience;
  cards: ComplianceRequirementCard[];
  signalBundle: SignalBundleMeta | null;
  readinessSnapshot?: unknown;
  linkedSignals?: ReadonlyArray<{ ruleId: string; signalIds: string[]; cardTitle: string }>;
  setHashStatus: (s: "server-verified" | "client-fallback") => void;
}): Promise<void> {
  const apiInput = {
    projectName: input.projectName || "evidence-pack",
    audience: input.audience,
    configHash: input.appliedHash,
    cards: input.cards,
    signalBundle: input.signalBundle,
    readiness: input.readinessSnapshot ?? null,
    linkedSignals: input.linkedSignals ?? [],
  };
  const serverResult = await buildEvidencePack(apiInput);
  if (serverResult) {
    triggerDownload(
      `regpulse-${slugForDownload(input.projectName)}-${stamp()}.json`,
      JSON.stringify(serverResult.pack, null, 2),
    );
    input.setHashStatus("server-verified");
    return;
  }
  // Fallback — same shape as before W7-slice-b2 with sha256: null.
  const pack = clientFallbackPack(input);
  triggerDownload(
    `regpulse-${slugForDownload(input.projectName)}-${stamp()}.json`,
    JSON.stringify(pack, null, 2),
  );
  input.setHashStatus("client-fallback");
}

/**
 * Backwards-compatible client-side pack. Kept as a fallback path for
 * the case where the worker is unreachable. Disclaimer is the FIRST
 * key (insertion order is the contract) — any consumer that strips
 * fields will still see it as the leading byte.
 */
function clientFallbackPack(input: {
  projectName: string;
  appliedHash: string | null;
  audience: Audience;
  cards: ComplianceRequirementCard[];
  signalBundle: SignalBundleMeta | null;
  readinessSnapshot?: unknown;
  linkedSignals?: ReadonlyArray<{ ruleId: string; signalIds: string[]; cardTitle: string }>;
}): Record<string, unknown> {
  return {
    disclaimer:
      "ADVISORY ONLY. The contents of this evidence pack are advisory " +
      "outputs of an analytical pipeline. Human review by a qualified " +
      "compliance lead is required before any approval, filing, or " +
      "external communication. No statement here constitutes a regulatory " +
      "decision or sign-off.",
    generatedAt: new Date().toISOString(),
    contractVersion: "0.1.1-w7b2",
    packHashStrategy: "client-side-only-no-r2-hash",
    sha256: null,
    scope: {
      projectName: input.projectName,
      audienceProfile: input.audience,
      configHash: input.appliedHash,
      signalBundle: input.signalBundle,
    },
    readiness: input.readinessSnapshot ?? null,
    cards: input.cards,
    linkedSignals: input.linkedSignals ?? [],
  };
}

function triggerDownload(filename: string, body: string): void {
  const blob = new Blob([body], { type: "application/json;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function stamp(): string {
  return new Date().toISOString().replace(/[:.]/g, "-");
}

function slugForDownload(projectName: string): string {
  return (projectName || "evidence-pack").replace(/[^a-z0-9\-_]+/gi, "_");
}

const colStyle: React.CSSProperties = {
  background: "var(--color-surface)",
  border: "1px solid var(--color-border)",
  borderRadius: "var(--radius-4)",
  padding: "var(--space-8)",
};

const colTitle: React.CSSProperties = {
  margin: "0 0 var(--space-8) 0",
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "var(--color-text-secondary)",
};

const dlRow: React.CSSProperties = {
  marginBottom: 6,
};
const dt: React.CSSProperties = {
  fontSize: 10,
  textTransform: "uppercase",
  color: "var(--color-text-secondary)",
  letterSpacing: "0.05em",
};
const dd: React.CSSProperties = {
  margin: "1px 0 0 0",
  fontSize: 11,
  color: "var(--color-text-primary)",
};
